// src/admin/pages/Categories.tsx
import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { Plus, Search, Edit2, Trash2, ChevronRight, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { CategoryWithSubcategories, Subcategory } from '../../types';
import * as LucideIcons from 'lucide-react'; // Import all Lucide icons
import { adminApi } from '../../utils/api';

export const Categories = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingSubcategory, setEditingSubcategory] = useState<{
    categoryId: number;
    subcategory: Subcategory;
  } | null>(null);
  const [categories, setCategories] = useState<CategoryWithSubcategories[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch categories and subcategories from backend
  useEffect(() => {
    const fetchCategories = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await adminApi.categories.getAll();
        setCategories(response.data);
      } catch (err: any) {
        console.error(err);
        setError(err.message || 'Failed to fetch categories.');
        toast.error(err.message || 'Failed to fetch categories.');
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Handle deletion of a subcategory
  const handleDeleteSubcategory = async (categoryId: number, subcategoryId: number) => {
    if (window.confirm('Are you sure you want to delete this subcategory?')) {
      try {
        await adminApi.categories.deleteSubcategory(categoryId, subcategoryId);
        // Update local state
        setCategories(prev =>
          prev.map(cat => {
            if (cat.id === categoryId) {
              return {
                ...cat,
                subcategories: cat.subcategories.filter(sub => sub.id !== subcategoryId)
              };
            }
            return cat;
          })
        );
        toast.success('Subcategory deleted successfully');
      } catch (err: any) {
        console.error(err);
        toast.error(err.message || 'Error deleting subcategory');
      }
    }
  };

  // Handle form submission for adding/editing subcategories
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const name = (formData.get('name') as string).trim();
    const description = (formData.get('description') as string).trim();

    if (!name) {
      toast.error('Name is required.');
      return;
    }

    try {
      if (editingSubcategory) {
        // Edit existing subcategory
        const { categoryId, subcategory } = editingSubcategory;
        const response = await adminApi.categories.updateSubcategory(
          categoryId,
          subcategory.id,
          { name, description }
        );
        // Update local state
        setCategories(prev =>
          prev.map(cat => {
            if (cat.id === categoryId) {
              return {
                ...cat,
                subcategories: cat.subcategories.map(sub =>
                  sub.id === subcategory.id ? { ...response.data, category_id: categoryId } as Subcategory : sub
                )
              };
            }
            return cat;
          })
        );
        toast.success('Subcategory updated successfully');
      } else {
        // Add new subcategory
        const categoryId = parseInt(formData.get('categoryId') as string, 10);
        const response = await adminApi.categories.createSubcategory(
          categoryId,
          { name, description }
        );
        // Update local state
        setCategories(prev =>
          prev.map(cat => {
            if (cat.id === categoryId) {
              return {
                ...cat,
                subcategories: [...cat.subcategories, { ...response.data, category_id: categoryId } as Subcategory]
              };
            }
            return cat;
          })
        );
        toast.success('Subcategory created successfully');
      }

      // Reset form
      setShowForm(false);
      setEditingSubcategory(null);
      form.reset();
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || 'Error saving subcategory');
    }
  };

  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="h-screen flex flex-col">
        <div className="flex-none p-6 space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-semibold text-gray-800">Categories</h2>
            {/* Remove Add Category button since adding new categories is not allowed */}
          </div>

          {/* Search Bar */}
          <div className="relative max-w-md">
            <input
              type="text"
              placeholder="Search categories..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
            />
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>

          {/* Button to Add Subcategory */}
          <div className="flex justify-end">
            <button
              onClick={() => {
                setEditingSubcategory(null);
                setShowForm(true);
              }}
              className="bg-pink-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-pink-600 transition-colors"
            >
              <Plus className="h-5 w-5" />
              Add Subcategory
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          <div className="h-full overflow-auto px-6 pb-6">
            {loading ? (
              <div className="flex justify-center items-center h-full">
                <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-12 w-12"></div>
              </div>
            ) : error ? (
              <div className="flex justify-center items-center h-full text-red-500">
                <p>{error}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredCategories.map((category) => (
                  <div
                    key={category.id}
                    className="bg-white rounded-lg shadow-sm overflow-hidden"
                  >
                    <div className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center gap-3">
                          <h3 className="text-lg font-semibold text-gray-900">{category.name}</h3>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Optionally, add Edit/Delete for main categories if needed */}
                          {/* Currently, adding/deleting main categories is not allowed */}
                        </div>
                      </div>

                      {category.description && (
                        <p className="text-gray-600 text-sm mb-4">{category.description}</p>
                      )}

                      {/* Subcategories */}
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-gray-700">Subcategories</h4>
                        <div className="space-y-1">
                          {category.subcategories && category.subcategories.length > 0 ? (
                            category.subcategories.map((sub) => (
                              <div
                                key={sub.id}
                                className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm"
                              >
                                <span>{sub.name}</span>
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => {
                                      setEditingSubcategory({ 
                                        categoryId: category.id, 
                                        subcategory: { ...sub, category_id: category.id } as Subcategory 
                                      });
                                      setShowForm(true);
                                    }}
                                    className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                    title="Edit Subcategory"
                                  >
                                    <Edit2 className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteSubcategory(category.id, sub.id)}
                                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                                    title="Delete Subcategory"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            ))
                          ) : (
                            <p className="text-gray-500 text-sm">No subcategories available.</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* If no categories found */}
                {filteredCategories.length === 0 && !loading && (
                  <div className="p-6 text-center text-gray-500">
                    No categories match your search criteria.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Subcategory Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold">
                {editingSubcategory ? 'Edit Subcategory' : 'Add Subcategory'}
              </h3>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingSubcategory(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* If adding a new subcategory, select the parent category */}
              {!editingSubcategory && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Parent Category
                  </label>
                  <select
                    name="categoryId"
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Subcategory Name
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  defaultValue={editingSubcategory?.subcategory.name || ''}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  name="description"
                  rows={3}
                  defaultValue={editingSubcategory?.subcategory.description || ''}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                />
              </div>

              <div className="flex justify-end gap-4 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingSubcategory(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600"
                >
                  {editingSubcategory ? 'Update Subcategory' : 'Create Subcategory'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Loader CSS */}
      <style>{`
        .loader {
          border-top-color: #3498db;
          animation: spinner 1.5s linear infinite;
        }

        @keyframes spinner {
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </AdminLayout>
  );
};